import { useEffect } from 'react'
import { GetServerSidePropsContext } from 'next'
import { z } from 'zod'
import { addPageAction } from '@mc/monitoring'
import { getBucket, getExperimentVariables } from '@mc/next-growthbook'
import { reportError } from '@mc/next-newrelic'

// https://app.growthbook.io/features/mccerts-2882_subscription-price-test_v1
export const MCCERTS_2882_SUBSCRIPTION_PRICE_TEST_FLAG =
  'mccerts-2882_subscription-price-test_v1'

export type SubscriptionPriceTestBucket = 'control' | 'variant_1'

const PriceTestVariablesSchema = z.object({
  plan_slug: z.string().optional(),
})

export const getPriceTest = async (
  ctx: GetServerSidePropsContext,
): Promise<{
  bucket: SubscriptionPriceTestBucket | undefined
  planSlug: string | undefined
}> => {
  const bucket =
    (await getBucket<SubscriptionPriceTestBucket>(
      ctx,
      MCCERTS_2882_SUBSCRIPTION_PRICE_TEST_FLAG,
    )) || undefined

  if (!bucket || bucket === 'control') {
    return { bucket, planSlug: undefined }
  }

  const variables = await getExperimentVariables(
    ctx,
    MCCERTS_2882_SUBSCRIPTION_PRICE_TEST_FLAG,
  )

  if (!variables) return { bucket, planSlug: undefined }

  const validated = PriceTestVariablesSchema.safeParse(variables)

  if (!validated.success) {
    reportError(validated.error, {
      message: 'Invalid mccerts-2882 subscription price test variables',
      flag: MCCERTS_2882_SUBSCRIPTION_PRICE_TEST_FLAG,
      variables: JSON.stringify(variables),
    })
    return { bucket, planSlug: undefined }
  }

  return { bucket, planSlug: validated.data.plan_slug }
}

export function usePriceTestFallbackTelemetry(
  planSlug: string | null | undefined,
  servedSlug: string | undefined,
) {
  useEffect(() => {
    if (planSlug && servedSlug && servedSlug !== planSlug) {
      addPageAction('mccerts_2882_plan_slug_fallback', {
        requested: planSlug,
        served: servedSlug,
      })
    }
  }, [planSlug, servedSlug])
}
